import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useOrgStudentDetail, useOrgStudentMutations } from '@/lib/hooks/useOrg';
import { StudentDetailView } from '@/features/OrgLayout/student/StudentDetailView';

export const Route = createFileRoute('/org/batches/$batchId/students/$userId')({
  component: StudentDetailPage,
});

function StudentDetailPage() {
  const { batchId, userId } = Route.useParams();
  const { data, isLoading, isError, refetch } = useOrgStudentDetail(batchId, userId);
  const m = useOrgStudentMutations(batchId, userId);
  const navigate = useNavigate();
  // The readiness evidence links are built by the view as plain hrefs; pushing
  // them through history keeps them client-side navigations rather than full
  // page loads, without the view having to know about the router.
  const router = useRouter();

  if (isLoading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-6 w-72" />
        <Skeleton className="h-48 rounded-[24px]" />
        <Skeleton className="h-32 rounded-[24px]" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="flex flex-col items-center py-16 text-center">
        <p className="text-[#6b7280]">Couldn&apos;t load this student.</p>
        <Button className="mt-4" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-[#6b7280]">
        <button type="button" onClick={() => navigate({ to: '/org/batches' })} className="hover:text-[#111827]">Batches</button>
        <span className="text-[#9ca3af]">›</span>
        <button type="button" onClick={() => navigate({ to: '/org/batches/$batchId', params: { batchId } })} className="hover:text-[#111827]">{data.batch_name}</button>
        <span className="text-[#9ca3af]">›</span>
        <span className="font-medium text-[#111827]">{data.name}</span>
      </nav>

      <StudentDetailView
        data={data}
        onAddNote={(c) => m.createNote.mutate(c)}
        addNotePending={m.createNote.isPending}
        onToggleShortlist={() => m.setShortlist.mutate(!data.shortlisted)}
        onToggleFlag={() => m.setFlag.mutate(!data.flagged)}
        shortlistPending={m.setShortlist.isPending}
        flagPending={m.setFlag.isPending}
        onNavigate={(href) => router.history.push(href)}
      />
    </div>
  );
}
